import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { CalendarRange } from 'lucide-react';

const rm = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Row({ label, value, testId, bold }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-slate-600 dark:text-slate-400">{label}</span>
      <span className="tabular-nums text-slate-900 dark:text-slate-100" data-testid={testId}>{rm(value)}</span>
    </div>
  );
}

// Year-to-date totals for the payslip's employee (Jan → payslip month). Mirrors the PDF footer.
export function YtdSummaryCard({ payslipId }) {
  const [ytd, setYtd] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!payslipId) return;
    setYtd(null); setError(false);
    axios.get(`${API}/payslips/${payslipId}/ytd`).then((r) => setYtd(r.data)).catch(() => setError(true));
  }, [payslipId]);

  if (error) return null;
  return (
    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4" data-testid="ytd-summary-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-slate-500" />
          Year-to-Date {ytd ? `(Jan – ${MONTHS[ytd.through_month - 1]} ${ytd.year})` : ''}
        </h4>
        {ytd && <span className="text-xs text-slate-500" data-testid="ytd-months-count">{ytd.months_count} payslip{ytd.months_count === 1 ? '' : 's'}</span>}
      </div>
      {!ytd ? <p className="text-xs text-slate-500">Loading…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Earnings</p>
            <Row label="Gross earnings" value={ytd.gross_salary} testId="ytd-gross" bold />
            <Row label="Basic salary" value={ytd.basic_salary} testId="ytd-basic" />
            {ytd.allowances > 0 && <Row label="Allowances" value={ytd.allowances} testId="ytd-allowances" />}
            {ytd.overtime > 0 && <Row label="Overtime" value={ytd.overtime} testId="ytd-overtime" />}
            {ytd.bonus > 0 && <Row label="Bonus / incentive" value={ytd.bonus} testId="ytd-bonus" />}
            {ytd.claims_reimbursement > 0 && <Row label="Claims reimbursed" value={ytd.claims_reimbursement} testId="ytd-claims" />}
            <Row label="Net pay" value={ytd.net_salary} testId="ytd-net" bold />
          </div>
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Statutory (employee / employer)</p>
            <div className="flex justify-between gap-3"><span className="text-slate-600 dark:text-slate-400">EPF</span><span className="tabular-nums" data-testid="ytd-epf">{rm(ytd.epf_employee)} / {rm(ytd.epf_employer)}</span></div>
            <div className="flex justify-between gap-3"><span className="text-slate-600 dark:text-slate-400">SOCSO</span><span className="tabular-nums" data-testid="ytd-socso">{rm(ytd.socso_employee)} / {rm(ytd.socso_employer)}</span></div>
            <div className="flex justify-between gap-3"><span className="text-slate-600 dark:text-slate-400">EIS</span><span className="tabular-nums" data-testid="ytd-eis">{rm(ytd.eis_employee)} / {rm(ytd.eis_employer)}</span></div>
            <Row label="PCB / MTD" value={ytd.pcb_tax} testId="ytd-pcb" />
            {(ytd.skbkk_employee > 0 || ytd.skbkk_employer > 0) && (
              <div className="flex justify-between gap-3"><span className="text-slate-600 dark:text-slate-400">SKBKK</span><span className="tabular-nums" data-testid="ytd-skbkk">{rm(ytd.skbkk_employee)} / {rm(ytd.skbkk_employer)}</span></div>
            )}
            {ytd.hrdf > 0 && <Row label="HRDF levy (employer)" value={ytd.hrdf} testId="ytd-hrdf" />}
            <Row label="Total deductions" value={ytd.total_deductions} testId="ytd-total-deductions" bold />
          </div>
        </div>
      )}
    </div>
  );
}
